import { React, useEffect, useState } from 'react';
import TableSkeleton from '../../../../components/SkeletonLoader/TableSkeleton';
import ReportFilter from './components/ReportFilter';
import GrowthReportTable from './components/GrowthReportTable';
import { useGetGrowthReportValuesMutation } from '../../../../features/authentication/persistApiSlice';
import { usePdfGeneratorMutation } from '../../../../features/authentication/pdfGeneratorApiSlice';
import HeaderToolbarContent from '../../../../components/TimeOnTaskReport/HeaderToolbar';
import SkeletonGrid from '../../../../components/SkeletonLoader/SkeletonGrid';
import { convertToCSV, downloadCSV, reorderObjectProperties } from '../../../assignments/utils';
import { useSelector } from 'react-redux';
import { selectCurrentUserData } from '../../../../features/authentication/authSlice';

const GrowthReport = () => {
  const user = useSelector(selectCurrentUserData);

  const [isPersistDataLoaded, setIsPersistDataLoaded] = useState(false);
  const [persistData, setPersistData] = useState({});
  const [isLoadingReportData, setIsLoadingReportData] = useState(false);
  const [growthReport, setGrowthReport] = useState({});
  const [isCsvGenerating, setIsCsvGenerating] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [selectedClassName, setSelectedClassName] = useState('');
  const [selectedStudentName, setSelectedStudentName] = useState('');
  const [selectedCourseName, setSelectedCourseName] = useState('');
  const [selectedPreTestName, setSelectedPreTestName] = useState(null);
  const [selectedMidTestName, setSelectedMidTestName] = useState(null);
  const [selectedPostTestName, setSelectedPostTestName] = useState(null);
  const [dateTimeBasedUniqueKey, setDateTimeBasedUniqueKey] = useState(new Date().getTime().toString());

  const [getGrowthReportPersistValues] = useGetGrowthReportValuesMutation();
  const [pdfGenerator] = usePdfGeneratorMutation();

  useEffect(() => {
    getReportOptions();
  }, []);

  const getReportOptions = async() => {
    let response = await getGrowthReportPersistValues().unwrap();
    setPersistData(response);
    setIsPersistDataLoaded(true);
  };

  const exportHandler = async() => {
    if(Object.keys(growthReport).length === 0)
      return;
    
    setIsCsvGenerating(true);
    const stripHtmlTags = (str) => str.replace(/<[^>]*>/g, '');

    const getGrowthCompletionData = (data, label) => {
      const includeMidTest = persistData.midTestID > 0;
    
      return {
        [`${label} Pre-Test`]: data.preTest,
        [`${label} Pre-Test Completion`]: data.preTestCompletion,
        ...(includeMidTest && {
          [`${label} Mid-Test`]: data.midTest,
          [`${label} Mid-Test Completion`]: data.midTestCompletion,
          ...(persistData.growth && { [`${label} Pre-Mid Growth`]: data.preMidGrowth }),
        }),
        [`${label} Post-Test`]: data.postTest,
        [`${label} Post-Test Completion`]: data.postTestCompletion,
        ...(includeMidTest &&
          persistData.growth && { [`${label} Mid-Post Growth`]: data.midPostGrowth }),
        [`${label} Pre-Post Growth`]: data.prePostGrowth,
      };
    };
  
    const getGrowthData = (data, label) => {
      const includeMidTest = persistData.midTestID > 0;
    
      return {
        [`${label} Pre-Test`]: data.preTest,
        ...(includeMidTest && {
          [`${label} Mid-Test`]: data.midTest,
          ...(persistData.growth && { [`${label} Pre-Mid Growth`]: data.preMidGrowth }),
        }),
        [`${label} Post-Test`]: data.postTest,
        ...(includeMidTest &&
          persistData.growth && { [`${label} Mid-Post Growth`]: data.midPostGrowth }),
        [`${label} Pre-Post Growth`]: data.prePostGrowth,
      };
    };

    const getAdditionalGrowthData = (item) => {
      let additionalData = {};

      if(persistData.studentID > 0)
      {
        let studentGrowth = getGrowthCompletionData(item.studentGrowth, 'Student');
        additionalData = { ...studentGrowth };
      }
      if(persistData.classID > 0)
      {
        let classGrowth = getGrowthCompletionData(item.classGrowth, 'Class');
        additionalData = { ...additionalData, ...classGrowth };
      }
      if(persistData.teacherID > 0)
      {
        let teacherGrowth = getGrowthCompletionData(item.teacherGrowth, 'Teacher');
        additionalData = { ...additionalData, ...teacherGrowth };
      }
      if(persistData.schoolID > 0)
      {
        let schoolGrowth = getGrowthCompletionData(item.schoolGrowth, 'School');
        additionalData = { ...additionalData, ...schoolGrowth };
      }
      if(persistData.districtID > 0)
      {
        let districtGrowth = getGrowthCompletionData(item.districtGrowth, 'District');
        additionalData = { ...additionalData, ...districtGrowth };
      }

      return additionalData;
    };

    const reportDetails = {
      Report : 'Comprehensive Growth Comparison Report',
      District : districtName,
      Location : locationName,
      Teacher : `${user?.LastName} ${user?.FirstName}`,
      Class : selectedClassName,
      Student : selectedStudentName,
      Course : selectedCourseName,
      'Pre Test' : stripHtmlTags(selectedPreTestName),
      ...(persistData.midTestID > 0 ? { 'Mid Test' : stripHtmlTags(selectedMidTestName) } : {}),
      'Post Test' : stripHtmlTags(selectedPostTestName)
    };

    const totals = [];
    const students = [];
    const topics = [];
    const standards = [];
    const attributes = [];
    const questions = [];
    
    growthReport.totals?.forEach((item) => {
      let additionalData = {};
      if(persistData.studentID > 0)
      {
        let studentGrowth = getGrowthCompletionData(item.studentGrowth, 'Student');
        additionalData = { ...studentGrowth };
      }
      if(persistData.classID > 0)
      {
        let classGrowth = getGrowthCompletionData(item.classGrowth, 'Class');
        additionalData = { ...additionalData, ...classGrowth };
      }
      if(persistData.teacherID > 0)
      {
        let teacherGrowth = getGrowthCompletionData(item.teacherGrowth, 'Teacher');
        additionalData = { ...additionalData, ...teacherGrowth };
      }
      if(persistData.schoolID > 0)
      {
        let schoolGrowth = getGrowthCompletionData(item.schoolGrowth, 'School');
        additionalData = { ...additionalData, ...schoolGrowth };
      }
      if(persistData.districtID > 0)
      {
        let districtGrowth = getGrowthCompletionData(item.districtGrowth, 'District');
        additionalData = { ...additionalData, ...districtGrowth };
      }
      
      let data = {
        '': item.scoreType == 'percentage' ? 'Total (Percentage)' : 'Total (Points)',
        'Pre-Test Max Points' : item.preTestMaxPoints,
        ...(persistData.midTestID > 0 && {'Mid-Test Max Points' : item.midTestMaxPoints}),
        'Post-Test Max Points' : item.postTestMaxPoints,
        ...additionalData
      };

      totals.push(data);
    });
  
    if(persistData.students)
    {
      growthReport.students?.forEach((item) => {
        let additionalData = {};
        if(persistData.studentID > 0)
        {
          let studentGrowth = getGrowthData(item.studentGrowth, 'Student');
          additionalData = { ...studentGrowth };
        }
        if(persistData.classID > 0)
        {
          let classGrowth = getGrowthData(item.classGrowth, 'Class');
          additionalData = { ...additionalData, ...classGrowth };
        }
        if(persistData.teacherID > 0)
        {
          let teacherGrowth = getGrowthData(item.teacherGrowth, 'Teacher');
          additionalData = { ...additionalData, ...teacherGrowth };
        }
        if(persistData.schoolID > 0)
        {
          let schoolGrowth = getGrowthData(item.schoolGrowth, 'School');
          additionalData = { ...additionalData, ...schoolGrowth };
        }
        if(persistData.districtID > 0)
        {
          let districtGrowth = getGrowthData(item.districtGrowth, 'District');
          additionalData = { ...additionalData, ...districtGrowth };
        }
        
        let data = {
          'First Name': item.firstName,
          'Last Name' : item.lastName,
          ...additionalData
        };
  
        students.push(data);
      });
    }

    if(persistData.topics && growthReport.topics?.length > 0)
    {
      growthReport.topics.forEach((topic) => {
        topic.score?.forEach((item) => {
          let data = {
            'Name': `"${topic.name.replace(/"/g, '""')}"`,
            'Pre-Test': `"${topic.preTestQuestionNos.join(', ')}"`,
            ...(persistData.midTestID > 0 &&
              {'Mid-Test' : `"${topic.midTestQuestionNos.join(', ')}"`}),
            'Post-Test' : `"${topic.postTestQuestionNos.join(', ')}"` ,
            'Score Type' : item.scoreType == 'points' ? 'Points' : '%',
            ...getAdditionalGrowthData(item)
          };
  
          topics.push(data);
        });
      });
    }

    if(persistData.standards && growthReport.standards?.length > 0)
    {
      growthReport.standards.forEach((standard) => {
        standard.score?.forEach((item) => { 
          let data = {
            'Name': `"${standard.name.replace(/"/g, '""')}"`,
            'Pre-Test': `"${standard.preTestQuestionNos.join(', ')}"`,
            ...(persistData.midTestID > 0 &&
                {'Mid-Test' : `"${standard.midTestQuestionNos.join(', ')}"`}),
            'Post-Test' : `"${standard.postTestQuestionNos.join(', ')}"` ,
            'Score Type' : item.scoreType == 'points' ? 'Points' : '%',
            ...getAdditionalGrowthData(item)
          };
    
          standards.push(data);
        });
      });
    }

    if(persistData.bloom && growthReport.attributes?.length > 0)
    {
      growthReport.attributes.forEach((attribute) => {
        attribute.score?.forEach((item) => {
          let data = {
            'Name': `"${attribute.name.replace(/"/g, '""')}"`,
            'Pre-Test': `"${attribute.preTestQuestionNos.join(', ')}"`,
            ...(persistData.midTestID > 0 &&
                  {'Mid-Test' : `"${attribute.midTestQuestionNos.join(', ')}"`}),
            'Post-Test' : `"${attribute.postTestQuestionNos.join(', ')}"` ,
            'Score Type' : item.scoreType == 'points' ? 'Points' : '%',
            ...getAdditionalGrowthData(item)
          };
      
          attributes.push(data);
        });
      });
    }

    if(persistData.questions)
    {
      growthReport.questions?.forEach((question) => {
        question.score?.forEach((item) => {
          let data = {
            'Pre-Test': question.preTestQuestionNo,
            ...(persistData.midTestID > 0 &&
                  {'Mid-Test' : question.midTestQuestionNo}),
            'Post-Test' : question.postTestQuestionNo,
            'Type' : question.type,
            'Max Points' : question.maxPoints,
            'Score Type' : item.scoreType == 'points' ? 'Points' : '%',
            ...getAdditionalGrowthData(item)
          };
      
          questions.push(data);
        });
      });
    }
    
    const newData = {
      ReportOptions : reportDetails,
      ...(growthReport.totals?.length > 0 && {
        '' : totals
      }),
      ...(persistData.students && growthReport.students?.length > 0 && {
        'Students' : students
      }),
      ...(persistData.topics && growthReport.topics?.length > 0 && {
        'Topics' : topics
      }),
      ...(persistData.standards && growthReport.standards?.length > 0 && {
        'Standards' : standards
      }),
      ...(persistData.bloom && growthReport.attributes?.length > 0 && {
        'Attributes' : attributes
      }),
      ...(persistData.questions && growthReport.questions?.length > 0 && {
        'Questions' : questions
      })
    };

    const newDataWithReportOptionsFirst = reorderObjectProperties(newData, 'ReportOptions');
    const csvData = convertToCSV(newDataWithReportOptionsFirst);
    downloadCSV(csvData, 'Comprehensive Growth Comparison Report' );
    setIsCsvGenerating(false);
  };

  const printHandler = async () => {
    if(Object.keys(growthReport).length === 0)
      return;

    setDateTimeBasedUniqueKey(new Date().getTime().toString());

    setTimeout(async() => {
      setIsPdfGenerating(true);
      const contentWrapper = document.getElementById('reportWrapper');
      const htmlString = contentWrapper.innerHTML;
  
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = htmlString;
  
      const ferpaContainer = tempContainer.querySelector('#buttonGroup');
      if (ferpaContainer) {
        ferpaContainer.classList.add('d-none');
      }
  
      const buttonElements = tempContainer.querySelectorAll('button');
      buttonElements.forEach((svgElement) => {
        svgElement.classList.add('d-none');
      });
  
      const reportCriteriaBtn = tempContainer.querySelector('#reportCriteriaBtn');
      if(reportCriteriaBtn) {
        reportCriteriaBtn.classList.add('d-none');
      }
  
      const reportCriteria = tempContainer.querySelector('#reportCriteria');
      if(reportCriteria) {
        reportCriteria.classList.remove('d-none');
      }
  
      const infoIcon = tempContainer.querySelector('#infoIcon');
      if(infoIcon) {
        infoIcon.classList.add('d-none');
      }
  
      const growthTables = tempContainer.querySelectorAll('#growthTable');
      growthTables.forEach((table) => {
        table.classList.remove('growth-table-scroll-bar');
      }); 
  
      const base64Encoded = btoa(tempContainer.innerHTML);
      const options = JSON.stringify({
        htmlContent: base64Encoded,
        OutputFileName: 'GrowthReport',
        IsLandscape: true,
        PageSize: 0
      });
  
      try {
        var pdfBlob = await pdfGenerator(options).unwrap();
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'Comprehensive_Growth_Comparison_Report.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(pdfUrl);
        setIsPdfGenerating(false);
      } catch (error) {
        setIsPdfGenerating(false);
        console.error(error);
      }
    },0);
  };

  return (
    <div className="white-card">
      <div id="reportWrapper">
        <HeaderToolbarContent
          reportTitle='Comprehensive Growth Comparison Report'
          exportHandler={exportHandler}
          isExporting={isCsvGenerating}
          printHandler={printHandler}
          isDownloading={isPdfGenerating}
          hideClassDropdown={true}
          isPageLoading={Object.keys(growthReport).length === 0}
        />
        {isPersistDataLoaded ?
          <ReportFilter
            dateTimeBasedUniqueKey={dateTimeBasedUniqueKey}
            isLoadingReportData={isLoadingReportData}
            setIsLoadingReportData={setIsLoadingReportData}
            persistData={persistData}
            setPersistData={setPersistData}
            setGrowthReport={setGrowthReport}
            setDisplayText={setDisplayText}
            displayText={displayText}
            setDistrictName={setDistrictName}
            districtName={districtName}
            setLocationName={setLocationName}
            locationName={locationName}
            setSelectedClassName={setSelectedClassName}
            selectedClassName={selectedClassName}
            setSelectedStudentName={setSelectedStudentName}
            selectedStudentName={selectedStudentName}
            setSelectedCourseName={setSelectedCourseName}
            selectedCourseName={selectedCourseName}
            setSelectedPreTestName={setSelectedPreTestName}
            selectedPreTestName={selectedPreTestName}
            setSelectedMidTestName={setSelectedMidTestName}
            selectedMidTestName={selectedMidTestName}
            setSelectedPostTestName={setSelectedPostTestName}
            selectedPostTestName={selectedPostTestName}
          />
          :
          <div id='reportFilterSkeleton' className='pt-4'>
            <SkeletonGrid colCount={3} rowCount={3} className={'mt-5 p-2'}/>
          </div>
        }
        <div id='reportData' className='mt-4'>
          {isLoadingReportData ?
            (
              <>
                {[...Array(4)].map((_, index) => (
                  <div key={'Skl'+index} className='pt-4'>
                    <TableSkeleton key={index} rowCount={4} colCount={6} />
                  </div>
                ))}     
              </>  
            ):(
              <>
                <GrowthReportTable
                  persistData={persistData}
                  growthReport={growthReport}
                />
              </>
            )
          }
        </div>
      </div>
    </div>
  );
};

export default GrowthReport;
